import { HiCurrencyDollar, HiTrendingUp, HiShoppingBag, HiReceiptTax, HiCube, HiTag } from 'react-icons/hi';
import { formatMoney, formatNumber } from '../../utils/reportFormat';

const tones = {
  primary: 'bg-primary-50 text-primary-600 shadow-primary-500/10',
  green: 'bg-emerald-50 text-emerald-600 shadow-emerald-500/10',
  gold: 'bg-gold-100 text-gold-600 shadow-gold-500/10',
  blue: 'bg-blue-50 text-blue-600 shadow-blue-500/10',
  red: 'bg-red-50 text-red-600 shadow-red-500/10',
};

function KPI({ title, value, hint, icon: Icon, tone, delay }) {
  return (
    <div
      className="bg-white rounded-2xl shadow-sm border border-primary-100/40 p-5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 animate-fadeInUp opacity-0"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1 min-w-0">
          <p className="text-xs md:text-sm text-gray-500 font-medium">{title}</p>
          <p className="text-xl md:text-2xl font-bold text-gray-800 truncate" dir="ltr" style={{ textAlign: 'right' }}>
            {value}
          </p>
          {hint && <p className="text-xs text-gray-400 font-medium">{hint}</p>}
        </div>
        <div className={`p-3 rounded-xl shadow-lg flex-shrink-0 ${tones[tone]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <div className="p-2 rounded-lg bg-primary-50 text-primary-500">
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <p className="text-[11px] text-gray-400">{label}</p>
        <p className="text-sm font-bold text-gray-800">{value}</p>
      </div>
    </div>
  );
}

export default function ReportKPIs({ summary, bySource }) {
  const sourceTotal = bySource.pos.revenue + bySource.mondoway.revenue;
  const posShare = sourceTotal > 0 ? (bySource.pos.revenue / sourceTotal) * 100 : 0;
  const loss = summary.profit < 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KPI
          title="إجمالي الإيرادات"
          value={formatMoney(summary.revenue)}
          hint="صافي مبيعات التصنيفات المحددة"
          icon={HiCurrencyDollar}
          tone="primary"
          delay={0}
        />
        <KPI
          title="الربح التقديري"
          value={formatMoney(summary.profit)}
          hint={`هامش ${summary.margin}% · تكلفة ${formatMoney(summary.cost)}`}
          icon={loss ? HiReceiptTax : HiTrendingUp}
          tone={loss ? 'red' : 'green'}
          delay={60}
        />
        <KPI
          title="عدد العمليات"
          value={formatNumber(summary.salesCount)}
          hint={`${formatNumber(summary.unitsSold)} وحدة مباعة`}
          icon={HiShoppingBag}
          tone="gold"
          delay={120}
        />
        <KPI
          title="متوسط قيمة البيع"
          value={formatMoney(summary.avgSaleValue)}
          hint="لكل عملية بيع"
          icon={HiReceiptTax}
          tone="blue"
          delay={180}
        />
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-primary-100/40 p-4 flex flex-col lg:flex-row lg:items-center gap-4 animate-fadeIn">
        <div className="flex items-center gap-6 flex-wrap">
          <MiniStat icon={HiTag} label="التصنيفات" value={formatNumber(summary.categoriesCount)} />
          <MiniStat icon={HiCube} label="الوحدات" value={formatNumber(summary.unitsSold)} />
        </div>
        <div className="hidden lg:block w-px h-10 bg-gray-100" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between text-xs mb-1.5 gap-2 flex-wrap">
            <span className="flex items-center gap-1.5 text-gray-600">
              <span className="w-2.5 h-2.5 rounded-full bg-primary-500" />
              نقطة البيع
              <b className="text-gray-800">{formatMoney(bySource.pos.revenue)}</b>
              <span className="text-gray-400">({bySource.pos.salesCount})</span>
            </span>
            <span className="flex items-center gap-1.5 text-gray-600">
              <span className="w-2.5 h-2.5 rounded-full bg-gold-400" />
              مندواي
              <b className="text-gray-800">{formatMoney(bySource.mondoway.revenue)}</b>
              <span className="text-gray-400">({bySource.mondoway.salesCount})</span>
            </span>
          </div>
          <div className="h-2.5 rounded-full bg-gold-400 overflow-hidden" title="توزيع الإيرادات حسب المصدر">
            <div
              className="h-full bg-primary-500 rounded-full transition-all duration-700"
              style={{ width: `${sourceTotal > 0 ? posShare : 0}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
